import React, { useState, useEffect } from 'react';
import axios from 'axios';
import './Induction.css';

const API = process.env.REACT_APP_API_URL || 'http://localhost:3001/api';

export default function Induction({ user }) {
  const [view, setView] = useState('list');
  const [staff, setStaff] = useState([]);
  const [inductions, setInductions] = useState([]);
  const [selectedUser, setSelectedUser] = useState(null);
  const [activeInduction, setActiveInduction] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [expandedSection, setExpandedSection] = useState(null);

  useEffect(() => { fetchData(); }, []);

  const fetchData = async () => {
    try {
      const [usersRes, inductionsRes] = await Promise.all([
        axios.get(`${API}/users`),
        axios.get(`${API}/inductions`)
      ]);
      setStaff(usersRes.data.filter(u => u.id !== user.id));
      setInductions(inductionsRes.data);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  };

  const startInduction = async () => {
    if (!selectedUser) return;
    setSaving(true);
    try {
      const res = await axios.post(`${API}/inductions`, {
        user_id: selectedUser.id,
        inducted_by: user.id,
        induction_date: new Date().toISOString().split('T')[0],
        created_by: user.id,
      });
      const full = await axios.get(`${API}/inductions/${res.data.id}`);
      setActiveInduction(full.data);
      setView('active');
    } catch (err) { alert('Error starting induction.'); }
    finally { setSaving(false); }
  };

  const loadInduction = async (id) => {
    try {
      const res = await axios.get(`${API}/inductions/${id}`);
      setActiveInduction(res.data);
      setExpandedSection(null);
      setView('active');
    } catch (err) { alert('Error loading induction.'); }
  };

  const toggleSection = async (section) => {
    if (activeInduction.status === 'completed') return;
    try {
      await axios.patch(`${API}/inductions/${activeInduction.id}/section/${section.id}`, {
        completed: !section.completed,
        completed_by: user.id,
      });
      setActiveInduction(prev => ({
        ...prev,
        sections: prev.sections.map(s =>
          s.id === section.id ? { ...s, completed: !s.completed, completed_at: !s.completed ? new Date().toISOString() : null } : s
        )
      }));
    } catch (err) { alert('Error updating section.'); }
  };

  const employeeSign = async () => {
    try {
      await axios.patch(`${API}/inductions/${activeInduction.id}/employee-sign`, { signed_by: user.id });
      setActiveInduction(prev => ({ ...prev, employee_signed: true, employee_signed_at: new Date().toISOString() }));
    } catch (err) { alert('Error recording signature.'); }
  };

  const completeInduction = async () => {
    try {
      await axios.patch(`${API}/inductions/${activeInduction.id}/complete`, { signed_by: user.id });
      setActiveInduction(prev => ({ ...prev, company_rep_signed: true, status: 'completed', completed_at: new Date().toISOString() }));
      // Generate and save certificate
      try {
        await axios.post(`${API}/inductions/${activeInduction.id}/certificate`);
      } catch (certErr) {
        console.error('Certificate generation error:', certErr);
      }
      setView('complete');
      fetchData();
    } catch (err) { alert('Error completing induction.'); }
  };

  const completedCount = activeInduction?.sections?.filter(s => s.completed).length || 0;
  const totalCount = activeInduction?.sections?.length || 0;
  const allCompleted = completedCount === totalCount && totalCount > 0;
  const sortedSections = activeInduction?.sections?.slice().sort((a, b) =>
    (a.template_item?.section_number || 0) - (b.template_item?.section_number || 0)
  );

  if (loading) return <div className="ind-loading">Loading...</div>;

  // COMPLETE VIEW
  if (view === 'complete') return (
    <div className="ind-complete">
      <div className="ind-complete-icon">✓</div>
      <h2>INDUCTION COMPLETE</h2>
      <p>{activeInduction?.user?.first_name} {activeInduction?.user?.last_name}'s induction has been recorded in Vera.</p>
      <p className="ind-complete-date">Completed {new Date().toLocaleDateString('en-GB')}</p>
      <button className="ind-btn primary" onClick={() => { setView('list'); setActiveInduction(null); }}>BACK TO INDUCTIONS</button>
    </div>
  );

  // ACTIVE INDUCTION VIEW
  if (view === 'active' && activeInduction) return (
    <div className="ind-active">
      <div className="ind-active-header">
        <div className="ind-active-info">
          <h2>{activeInduction.user?.first_name} {activeInduction.user?.last_name}</h2>
          <span className="ind-active-role">{activeInduction.user?.job_title}</span>
          <span className="ind-active-date">{new Date(activeInduction.induction_date).toLocaleDateString('en-GB')}</span>
        </div>
        <div className="ind-progress">
          <div className="ind-progress-bar">
            <div className="ind-progress-fill" style={{ width: `${totalCount > 0 ? (completedCount / totalCount) * 100 : 0}%` }} />
          </div>
          <span className="ind-progress-text">{completedCount} / {totalCount} sections</span>
        </div>
      </div>

      <div className="ind-sections">
        {sortedSections?.map(section => {
          const isExpanded = expandedSection === section.id;
          const lines = section.template_item?.description?.split('\n') || [];
          return (
            <div key={section.id} className={`ind-section ${section.completed ? 'completed' : ''} ${section.template_item?.requires_signoff ? 'signoff' : ''} ${isExpanded ? 'expanded' : ''}`}>
              <div className="ind-section-check" onClick={() => toggleSection(section)}>
                {section.completed ? '✓' : section.template_item?.section_number}
              </div>
              <div className="ind-section-content">
                <div className="ind-section-header" onClick={() => setExpandedSection(isExpanded ? null : section.id)}>
                  <div className="ind-section-title">
                    {section.template_item?.title}
                    {section.template_item?.requires_signoff && <span className="ind-signoff-badge">SIGN OFF</span>}
                  </div>
                  <span className="ind-expand-arrow">{isExpanded ? '▲' : '▼'}</span>
                </div>
                {isExpanded && (
                  <div className="ind-section-body">
                    {lines.map((line, i) =>
                      line.trim() ? <p key={i} className="ind-content-line">{line}</p> : <div key={i} className="ind-content-gap" />
                    )}
                    {activeInduction.status !== 'completed' && (
                      <button
                        className={`ind-tick-btn ${section.completed ? 'ticked' : ''}`}
                        onClick={(e) => { e.stopPropagation(); toggleSection(section); }}
                      >
                        {section.completed ? '✓ COVERED — CLICK TO UNDO' : 'MARK AS COVERED'}
                      </button>
                    )}
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {allCompleted && activeInduction.status !== 'completed' && (
        <div className="ind-signoff">
          <h3>SIGN OFF</h3>
          <p>All sections have been covered. Both the employee and company representative must confirm below.</p>
          <div className="ind-signoff-row">
            <div className={`ind-signoff-btn ${activeInduction.employee_signed ? 'signed' : ''}`} onClick={!activeInduction.employee_signed ? employeeSign : undefined}>
              {activeInduction.employee_signed ? (
                <><span className="sig-check">✓</span><div><div className="sig-label">EMPLOYEE CONFIRMED</div><div className="sig-time">{new Date(activeInduction.employee_signed_at).toLocaleTimeString('en-GB')}</div></div></>
              ) : (
                <><span className="sig-icon">✍</span><div><div className="sig-label">EMPLOYEE SIGN OFF</div><div className="sig-sublabel">Tap to confirm all sections were covered</div></div></>
              )}
            </div>
            {activeInduction.employee_signed && user.role === 'admin' && (
              <div className={`ind-signoff-btn ${activeInduction.company_rep_signed ? 'signed' : 'company'}`} onClick={!activeInduction.company_rep_signed ? completeInduction : undefined}>
                {activeInduction.company_rep_signed ? (
                  <><span className="sig-check">✓</span><div><div className="sig-label">COMPANY REP CONFIRMED</div><div className="sig-time">{new Date(activeInduction.company_rep_signed_at).toLocaleTimeString('en-GB')}</div></div></>
                ) : (
                  <><span className="sig-icon">✍</span><div><div className="sig-label">COMPANY REP SIGN OFF</div><div className="sig-sublabel">{user.first_name} {user.last_name} — tap to complete</div></div></>
                )}
              </div>
            )}
          </div>
        </div>
      )}

      <button className="ind-back-btn" onClick={() => { setView('list'); setActiveInduction(null); fetchData(); }}>← BACK</button>
    </div>
  );

  // START INDUCTION VIEW
  if (view === 'start') return (
    <div className="ind-start">
      <h2>START NEW INDUCTION</h2>
      <p>Select the staff member being inducted today.</p>
      <div className="ind-staff-grid">
        {staff.map(s => (
          <div key={s.id} className={`ind-staff-card ${selectedUser?.id === s.id ? 'selected' : ''}`} onClick={() => setSelectedUser(s)}>
            <div className="ind-staff-avatar">{s.first_name[0]}{s.last_name[0]}</div>
            <div className="ind-staff-name">{s.first_name} {s.last_name}</div>
            <div className="ind-staff-role">{s.job_title}</div>
          </div>
        ))}
      </div>
      <div className="ind-start-actions">
        <button className="ind-btn secondary" onClick={() => setView('list')}>CANCEL</button>
        <button className={`ind-btn primary ${!selectedUser || saving ? 'disabled' : ''}`} onClick={startInduction} disabled={!selectedUser || saving}>
          {saving ? 'STARTING...' : 'START INDUCTION'}
        </button>
      </div>
    </div>
  );

  // LIST VIEW
  return (
    <div className="ind-list">
      <div className="ind-list-header">
        <p>{inductions.length} induction{inductions.length !== 1 ? 's' : ''} on record</p>
        {user.role === 'admin' && <button className="ind-btn primary" onClick={() => setView('start')}>+ NEW INDUCTION</button>}
      </div>
      {inductions.length === 0 ? (
        <div className="ind-empty"><p>No inductions recorded yet.</p></div>
      ) : (
        <div className="ind-records">
          {inductions.map(ind => (
            <div key={ind.id} className="ind-record" onClick={() => loadInduction(ind.id)}>
              <div className="ind-record-avatar">{ind.user?.first_name?.[0]}{ind.user?.last_name?.[0]}</div>
              <div className="ind-record-info">
                <div className="ind-record-name">{ind.user?.first_name} {ind.user?.last_name}</div>
                <div className="ind-record-meta">{ind.user?.job_title} — {new Date(ind.induction_date).toLocaleDateString('en-GB')}</div>
                <div className="ind-record-meta">Inducted by {ind.inducted_by?.first_name} {ind.inducted_by?.last_name}</div>
              </div>
              <div className={`ind-record-status ${ind.status === 'completed' ? 'completed' : 'in-progress'}`}>
                {ind.status === 'completed' ? 'COMPLETED' : 'IN PROGRESS'}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
